/**
 * PromotersStep.tsx - Fundraiser Builder Step 4:PromoterProgressModel Management
 *
 * This component handles the fourth and final step of the fundraiser builder
 * where users configure promoter settings, rewards, and invitation options.
 *
 * Key Features:
 * -PromoterProgressModel invitation and management
 * - Reward system configuration (by amount and rank)
 * -PromoterProgressModel goal setting and defaults
 * - Invitation link generation and sharing
 * - Reward tier management with validation
 * - Rank-based reward system with cascading logic
 * - Social sharing for promoter recruitment
 *
 * Configuration Sections:
 * -PromoterProgressModel Settings: Goals, invitations, and recruitment options
 * - Rewards by Amount: Achievement-based rewards for donation levels
 * - Rewards by Rank: Competitive ranking rewards with minimums
 * - Invitation Management: Link generation and sharing tools
 *
 * Reward Systems:
 * - Amount-based: Rewards for reaching donation thresholds
 * - Rank-based: Competitive rewards based on leaderboard position
 * - Validation: Ensures proper reward tier configuration
 * - Cascading: Handles promoters who don't meet minimums
 *
 * Props: None (uses MobX store for state management)
 *
 * Dependencies: MobX store, reward management components, social sharing
 */

import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { createPortal } from "react-dom";
import { Users, Plus, ChevronUp, ChevronDown, X } from "lucide-react";
import { fundraiserStore } from "../../stores/FundraiserStore";
import { AddPromoterDropdown } from "./DropdownButton";
import { AddMultiplePromotersModal } from "../AddMultiplePromotersModal";
import { AddSinglePromoterModal } from "../AddSinglePromoterModal";
import { EditPromoterModal } from "../EditPromoterModal";
import { Toast } from "../Toast";
import { SocialShareModal } from "../SocialShareModal";
import { PromoterActionsDropdown } from "../PromoterActionsDropdown";
import { RemovePromoterModal } from "../RemovePromoterModal";
import { FundRaiserStatus } from "../../enums";
import { ContactReferenceModel } from "@mindmemobile/mm-payment-modules";
import { CreateFundraiserModel, PromoterProgressModel } from "../../models";
import { NavigateFunction } from "react-router-dom";
import { AuthService, withRouter } from "@mindmemobile/mm-shared-global";
import ToastService from "../ToastService";

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const SectionTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SearchWrapper = styled.div`
  position: relative;
  width: 300px;

  @media (max-width: 640px) {
    width: 250px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 14px 20px 14px 48px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: 16px center;
  background-size: 20px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    color: #1e293b;
    background: #f1f5f9;
  }
`;

const DataTable = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: visible;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
`;

const TableHeader = styled.div<{ columns: string }>`
  display: grid;
  grid-template-columns: ${(props) => props.columns};
  padding: 20px 24px;
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
  border-bottom: 1px solid #e2e8f0;
  font-size: 14px;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SortableHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #10b981;
  }
`;

const SortIcon = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0.6;
`;

const TableRow = styled.div<{ columns: string }>`
  display: grid;
  grid-template-columns: ${(props) => props.columns};
  padding: 20px 24px;
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.2s ease;
  align-items: center;

  &:hover {
    background: linear-gradient(135deg, #f8fafc, #ffffff);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const PromoterInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const PromoterAvatarFallback = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000000;
  font-weight: 700;
  font-size: 14px;
  border: 2px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
`;

const PromoterAvatarContainer = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const PromoterAvatar = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ContactItem = styled.div`
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
`;

const ProgressContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProgressBar = styled.div`
  width: 120px;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ percentage: number }>`
  width: ${(props) => Math.min(props.percentage, 100)}%;
  height: 100%;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 4px;
  transition: width 0.8s ease;
`;

const ProgressText = styled.div`
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
  white-space: nowrap;
`;

const URLLink = styled.a`
  color: #3b82f6;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

const TotalDonationsValue = styled.div`
  font-weight: 700;
  color: #1e293b;
  font-size: 16px;
  text-align: left;
`;

const PromoterDetails = styled.div``;

const PromoterName = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
`;

const StatusBadge = styled.div<{ status: string }>`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${(props) => {
    switch (props.status) {
      case "active":
        return "background: #dcfce7; color: #166534;";
      case "inactive":
        return "background: #fef3c7; color: #92400e;";
      default:
        return "background: #f3f4f6; color: #374151;";
    }
  }}
`;

const ActionsContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding-left: 42px;
`;

const ActionsHeader = styled.div`
  padding-left: 42px;
`;

const NoResults = styled.div`
  padding: 40px;
  text-align: center;
  color: #64748b;
  font-size: 16px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #64748b;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
`;

const EmptyStateIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #94a3b8;
`;

const ModalButton = styled.button<{ variant?: "primary" }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${(props) =>
    props.variant === "primary"
      ? `
    background: #10b981;
    color: #ffffff;

    &:hover {
      background: #059669;
    }
  `
      : `
    background: #f1f5f9;
    color: #64748b;

    &:hover {
      background: #e2e8f0;
    }
  `}
`;

const Modal = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 999999;
  backdrop-filter: blur(4px);
  padding: 20px;
  overflow-y: auto;
  box-sizing: border-box;
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 24px;
  padding: 32px;
  width: 90%;
  max-width: 600px;
  max-height: 95vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const CloseButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: #e2e8f0;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 52px;
  height: 28px;
  cursor: pointer;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background-color: #10b981;
  }

  &:checked + span:before {
    transform: translateX(24px);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e1;
  transition: 0.3s;
  border-radius: 28px;

  &:before {
    position: absolute;
    content: "";
    height: 20px;
    width: 20px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

type SortField =
  | "name"
  | "email"
  | "goal"
  | "donations"
  | "progress"
  | "status"
  | null;
type SortDirection = "asc" | "desc";

type IPromotersStepProps = {
  navigate: NavigateFunction;
};

const PromotersStep = observer(({ navigate }: IPromotersStepProps) => {
  const { builderData } = fundraiserStore;
  const [showAddSingleModal, setShowAddSingleModal] = useState(false);
  const [showSelectModal, setShowSelectModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [promoterToRemove, setPromoterToRemove] =
    useState<PromoterProgressModel | null>(null);
  const [promoterToEdit, setPromoterToEdit] =
    useState<PromoterProgressModel | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSocialShareModal, setShowSocialShareModal] = useState(false);
  const [sharePromoter, setSharePromoter] =
    useState<PromoterProgressModel | null>(null);
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
 const baseUrl = fundraiserStore.getFundRaiserDomainBaseURL();

  // Check if we can add/edit promoters based on fundraiser status
  const canModifyPromoters =
    !builderData ||
    [
      FundRaiserStatus.Draft,
      FundRaiserStatus.Active,
      FundRaiserStatus.Inactive,
    ].includes(builderData.status as FundRaiserStatus);

  const promoters = builderData.promoterGridData || [];

  // Only show header if there are promoters
  const shouldShowHeader = promoters.length > 0;

  const handleAddPromoter = () => {
    setShowAddSingleModal(true);
  };

  const handleSelectContacts = (contacts: ContactReferenceModel[]) => {
    var allContacts = Array.from(
      new Map(
        fundraiserStore.contacts
          .filter(
            (contact) =>
              promoters.some((p) => p.contactId === contact.id) ||
              contacts.some((p) => p.id === contact.id)
          )
          .map((c) => [c.id, c]) // key by id
      ).values()
    );
    const existingContacts = new Set(
      promoters
        .filter((p) => p.contactId && p.fundraiserId === builderData.id)
        .map((p) => p.contactId)
    );

    const newContacts = allContacts.filter(
      (contact) => !existingContacts.has(contact.id)
    ).map((contact) => {
      contact.isNew = true;
      return contact;
    });

    const newPromoters = CreateFundraiserModel.deserializePromoters(
      builderData.id,
      fundraiserStore.getDefaultPromoterGoal(builderData.id),
      promoters.length,
      newContacts
    );
    fundraiserStore.updateBuilderField("promoterGridData", [...promoters, ...newPromoters]);
    ToastService.showSuccess(
      `${newContacts.length} promoter${newContacts.length !== 1 ? "s" : ""} added successfully`
    );
    setShowAddSingleModal(false);
    setShowSelectModal(false);
  };

  const handleRemovePromoter = (id: string) => {
    const updated = promoters.filter((p) => p.id !== id);
    fundraiserStore.updateBuilderField("promoterGridData", updated);
  };

  const handleMakeInactive = () => {
    if (promoterToRemove) {
      const newStatus = promoterToRemove.isActive === true;
      const updated = promoters.map((p) =>
        p.id === promoterToRemove.id ? { ...p, isActive: newStatus } : p
      );
      fundraiserStore.updateBuilderField("promoterGridData", updated);
      setShowRemoveModal(false);
      setPromoterToRemove(null);
      ToastService.showSuccess(
        `Promoter made ${newStatus ? "active" : "inactive"} successfully`
      );
    }
  };

  const handleCloseRemoveModal = () => {
    setShowRemoveModal(false);
    setPromoterToRemove(null);
  };

  // What a promoter has raised (the donations list carries no promoter id, so summing it was always $0).
  const getPromoterRemovalTotal = (promoterId: string) =>
    promoters.find((p) => p.id === promoterId)?.totalRaisedAmount || 0;

  const handleRemoveClick = (promoter: PromoterProgressModel) => {
    console.log("Remove clicked for promoter:", promoter.displayName); // Debug log
    if (!canModifyPromoters) {
      console.log("Cannot modify promoters"); // Debug log
      return;
    }

    // A promoter who has raised money cannot be removed; the modal offers "make inactive".
    const hasDonations =
      getPromoterRemovalTotal(promoter.id) > 0 || (promoter.totalDonations || 0) > 0;

    if (hasDonations) {
      // Show confirmation modal
      console.log("Showing remove modal"); // Debug log
      setPromoterToRemove(promoter);
      setShowRemoveModal(true);
    } else {
      // Remove immediately without confirmation
      console.log("Removing promoter immediately"); // Debug log
      handleRemovePromoter(promoter.id);
      ToastService.showSuccess("Promoter removed successfully");
    }
  };

  const handleEditPromoter = (promoter: PromoterProgressModel) => {
    // Clear any existing toast when opening edit modal
    setPromoterToEdit(promoter);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setPromoterToEdit(null);
  };

  const handleUpdatePromoter = (updatedPromoter: PromoterProgressModel) => {
    const updatedPromoters = CreateFundraiserModel.updatePromoter(
      promoters,
      updatedPromoter.id,
      updatedPromoter
    );
    fundraiserStore.updateBuilderField("promoterGridData", updatedPromoters);
    setShowEditModal(false);
    setPromoterToEdit(null);
    ToastService.showSuccess("Promoter updated successfully");
  };

  const handlePreviewPromoter = (promoter: PromoterProgressModel) => {
    const fundraiserId = builderData.id || "1";
    // Set the promoter context in the URL for proper routing
    // window.location.hash = `/fundraiser/${fundraiserId}?PID=${promoter.contactId}`;
    window.open(`${baseUrl}/fundraiser/${fundraiserId}`, "_blank");
  };

  const handleInvitePromoter = (promoter: PromoterProgressModel) => {
    const fundraiserId = builderData.id;
    window.open(`${baseUrl}/fundraiser/promoter-invite/${fundraiserId}`, "_blank");
  };

  const handlePromoterDashboard = (promoter: PromoterProgressModel) => {
    // Navigate to promoter dashboard page
    const fundraiserId = builderData.id;
    window.open(
      `${baseUrl}/fundraiser/promoter-dashboard/${fundraiserId}?PID=${promoter.contactId}`,
      "_blank"
    );
  };

  const handleSharePromoter = (promoter: PromoterProgressModel) => {
    setSharePromoter(promoter);
    // Set the selected fundraiser context for sharing
    fundraiserStore.selectedFundraiserId = builderData.id || "1";
    // Set URL parameter for promoter-specific sharing 
    const fundraiserId = builderData.id || "";
    const promoterUrl = `${baseUrl}/fundraiser/${fundraiserId}?PID=${promoter.contactId}`;
    setShowSocialShareModal(true);
  };

  const handleCopyPromoterLink = async (promoter: PromoterProgressModel) => {
    const baseUrl = fundraiserStore.getFundRaiserDomainBaseURL();
    const fundraiserId = builderData.id || "";
    const url = `${baseUrl}/fundraiser/${fundraiserId}?PID=${promoter.contactId}`;

    try {
      await navigator.clipboard.writeText(url);
      ToastService.showSuccess("Promoter link copied to clipboard");
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      ToastService.showSuccess("Promoter link copied to clipboard");
    }
  };

  const handleImageError = (promoterId: string) => {
    setImageErrors((prev) => {
      const next = new Set(prev);
      next.add(promoterId);
      return next;
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection(field === "donations" ? "desc" : "asc");
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const renderPromoterAvatar = (promoter: PromoterProgressModel) => {
    if (imageErrors.has(promoter.id)) {
      return (
        <PromoterAvatarFallback>
          {getInitials(promoter.displayName)}
        </PromoterAvatarFallback>
      );
    }

    return (
      <PromoterAvatar
        src={promoter.imageURL}
        alt={promoter.displayName}
        onError={() => handleImageError(promoter.id)}
      />
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const filteredPromoters = promoters.filter(
    (promoter: PromoterProgressModel) => {
      if (!searchTerm) return true;

      const searchLower = searchTerm.toLowerCase();
      return (
        promoter.displayName.toLowerCase().includes(searchLower) ||
        promoter.email.toLowerCase().includes(searchLower) ||
        promoter.mobile.includes(searchLower)
      );
    }
  );

  const getSortedPromoters = () => {
    let sorted = [...filteredPromoters];

    // Default to sorting by donations (totalRaised) descending
    if (sortField === "donations" || sortField === null) {
      sorted = sorted.sort((a, b) => {
        const comparison = a.totalRaisedAmount - b.totalRaisedAmount;
        return sortDirection === "asc" ? comparison : -comparison;
      });
    } else if (sortField === "name") {
      sorted = sorted.sort((a, b) => {
        const lastNameA = a.displayName.split(" ").pop()?.toLowerCase() || "";
        const lastNameB = b.displayName.split(" ").pop()?.toLowerCase() || "";
        const comparison = lastNameA.localeCompare(lastNameB);
        return sortDirection === "asc" ? comparison : -comparison;
      });
    } else if (sortField === "progress") {
      sorted = sorted.sort((a, b) => {
        const progressA = (a.totalRaisedAmount / a.goalAmount) * 100;
        const progressB = (b.totalRaisedAmount / b.goalAmount) * 100;
        const comparison = progressA - progressB;
        return sortDirection === "asc" ? comparison : -comparison;
      });
    } else if (sortField === "status") {
      sorted = sorted.sort((a, b) => {
        const statusA = a.isActive ? "active" : "inactive";
        const statusB = b.isActive ? "active" : "inactive";
        const comparison = statusA.localeCompare(statusB);
        return sortDirection === "asc" ? comparison : -comparison;
      });
    }

    return sorted;
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return (
        <SortIcon>
          <ChevronUp size={12} />
          <ChevronDown size={12} style={{ marginTop: "-2px" }} />
        </SortIcon>
      );
    }

    return (
      <SortIcon style={{ opacity: 1 }}>
        {sortDirection === "asc" ? (
          <ChevronUp size={16} />
        ) : (
          <ChevronDown size={16} />
        )}
      </SortIcon>
    );
  };

  const sortedPromoters = getSortedPromoters();

  // Define dropdown options - THIS IS THE KEY PART
  const dropdownOptions = [
    {
      id: "multiple",
      title: "Add Multiple Promoters",
      description: "Select contacts by type, list, interest or tag",
      icon: <Users size={16} />,
      onClick: () => {
        setShowSelectModal(true);
      },
    },
    {
      id: "single",
      title: "Add Single Promoter",
      description: "Add new or existing contact<br />from your contact list",
      icon: <Plus size={16} />,
      onClick: () => {
        handleAddPromoter();
      },
    },
  ];

  return (
    <StepContainer>
      {shouldShowHeader && (
        <SectionHeader>
          <SectionTitle>Promoters ({filteredPromoters.length})</SectionTitle>
          <RightControls>
            <SearchWrapper>
              <SearchInput
                placeholder="Search promoters..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <ClearButton onClick={clearSearch} title="Clear search">
                  <X size={16} />
                </ClearButton>
              )}
            </SearchWrapper>
            {canModifyPromoters && (
              <AddPromoterDropdown options={dropdownOptions} />
            )}
          </RightControls>
        </SectionHeader>
      )}

      {!shouldShowHeader ? (
        <EmptyState>
          <EmptyStateIcon>
            <Users size={40} />
          </EmptyStateIcon>
          <h4
            style={{ margin: "0 0 8px 0", fontSize: "22px", fontWeight: "700" }}
          >
            No Promoters Yet
          </h4>
          <br />
          <p style={{ margin: "0 0 8px 0", lineHeight: "1.5" }}>
            Add promoters who will help spread the word and raise funds for this
            fundraiser.
            <br />
            Each promoter can have a custom fundraising goal.
            {!canModifyPromoters && (
              <>
                <br />
                <br />
                New promoters cannot be added if the fundraiser status is
                completed.
              </>
            )}
          </p>
          {canModifyPromoters ? (
            <div style={{ marginTop: "32px" }}>
              <AddPromoterDropdown options={dropdownOptions} />
            </div>
          ) : (
            <div style={{ marginTop: "32px" }}>
              <div
                style={{
                  padding: "12px 24px",
                  background: "#f3f4f6",
                  color: "#9ca3af",
                  borderRadius: "8px",
                  fontWeight: "600",
                  opacity: 0.7,
                  cursor: "not-allowed",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Plus size={16} />
                Add Promoters
              </div>
            </div>
          )}
        </EmptyState>
      ) : shouldShowHeader ? (
        <DataTable
          style={{ minHeight: sortedPromoters.length > 0 ? "500px" : "auto" }}
        >
          <TableHeader columns="1.5fr 1.1fr 0.7fr 110px 260px">
            <SortableHeader onClick={() => handleSort("name")}>
              <span>Name</span>
              {renderSortIcon("name")}
            </SortableHeader>
            <SortableHeader onClick={() => handleSort("progress")}>
              <span>Progress</span>
              {renderSortIcon("progress")}
            </SortableHeader>
            <SortableHeader onClick={() => handleSort("donations")}>
              <span>DONATIONS</span>
              {renderSortIcon("donations")}
            </SortableHeader>
            <SortableHeader onClick={() => handleSort("status")}>
              <span>Status</span>
              {renderSortIcon("status")}
            </SortableHeader>
            <ActionsHeader>Actions</ActionsHeader>
          </TableHeader>

          {sortedPromoters.length === 0 ? (
            <NoResults>
              {searchTerm
                ? `No promoters found matching "${searchTerm}"`
                : "No promoters found"}
            </NoResults>
          ) : (
            sortedPromoters.map(
              (promoter: PromoterProgressModel, index: number) => {
                const percentage =
                  promoter.goalAmount > 0
                    ? (promoter.totalRaisedAmount / promoter.goalAmount) * 100
                    : 0;
                return (
                  <TableRow
                    key={promoter.id || index}
                    columns="1.5fr 1.1fr 0.7fr 110px 260px"
                  >
                    <PromoterInfo>
                      <PromoterAvatarContainer>
                        {renderPromoterAvatar(promoter)}
                      </PromoterAvatarContainer>
                      <PromoterDetails>
                        <PromoterName>{promoter.displayName}</PromoterName>
                        <ContactInfo>
                          <ContactItem>{promoter.mobile}</ContactItem>
                          <ContactItem>{promoter.email}</ContactItem>
                        </ContactInfo>
                      </PromoterDetails>
                    </PromoterInfo>

                    <ProgressContainer>
                      <ProgressBar>
                        <ProgressFill
                          percentage={Math.min(percentage || 0, 100)}
                        />
                      </ProgressBar>
                      {promoter.goalAmount > 0 && (
                        <ProgressText>
                          {percentage > 0 ? percentage.toFixed(1) : percentage}%
                        </ProgressText>
                      )}
                    </ProgressContainer>

                    <TotalDonationsValue>
                      {formatCurrency(promoter.totalRaisedAmount)}
                    </TotalDonationsValue>

                    <div style={{ textAlign: "left" }}>
                      <StatusBadge
                        status={promoter.isActive ? "active" : "inactive"}
                      >
                        {promoter.isActive ? "Active" : "Inactive"}
                      </StatusBadge>
                    </div>

                    <ActionsContainer>
                      <PromoterActionsDropdown
                        isNewPromoter={promoter.isNew}
                        onDashboard={() => handlePromoterDashboard(promoter)}
                        onDonate={() => handlePreviewPromoter(promoter)}
                        onInvite={() => handleInvitePromoter(promoter)}
                        onEdit={() => handleEditPromoter(promoter)}
                        onCopyLink={() => handleCopyPromoterLink(promoter)}
                        onShare={() => handleSharePromoter(promoter)}
                        onDelete={() => handleRemoveClick(promoter)}
                      />
                    </ActionsContainer>
                  </TableRow>
                );
              }
            )
          )}
        </DataTable>
      ) : null}

      {/* Add Single Promoter Modal */}
      <AddSinglePromoterModal
        promoters={promoters}
        isVisible={showAddSingleModal}
        onClose={() => setShowAddSingleModal(false)}
        updatePromoters={(updatedPromoters) =>
          fundraiserStore.updateBuilderField("promoterGridData", [
            ...updatedPromoters,
          ])
        }
        onSelect={handleSelectContacts}
        fundraiserId={builderData.id}
      />

      {/* EditIPromoter Modal */}
      <EditPromoterModal
        isVisible={showEditModal}
        onClose={handleCloseEditModal}
        promoter={promoterToEdit}
        onUpdate={handleUpdatePromoter}
      />

      {/* RemoveIPromoter Modal */}
      <RemovePromoterModal
        isVisible={showRemoveModal}
        promoter={promoterToRemove}
        onClose={handleCloseRemoveModal}
        onMakeInactive={handleMakeInactive}
        formatCurrency={formatCurrency}
        getRemovalTotal={getPromoterRemovalTotal}
      />

      {/* Select Multiple Promoters Modal */}
      <AddMultiplePromotersModal
        visible={showSelectModal}
        promoters={promoters}
        onClose={() => setShowSelectModal(false)}
        onSelect={handleSelectContacts}
        fundraiserId={builderData.id || "1"}
      />

      {/* Social Share Modal forIPromoter */}
      {showSocialShareModal &&
        createPortal(
          <SocialShareModal
            gallerySettings={fundraiserStore.gallerySettings}
            isVisible={showSocialShareModal}
            onClose={() => {
              setShowSocialShareModal(false);
              setSharePromoter(null);
            }}
            shortUniqueId={AuthService.shortUniqueId}
          />,
          document.body
        )}
    </StepContainer>
  );
});

export default withRouter(PromotersStep);
